import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { Icon } from '../icon';

const badge = cva(
  'inline-flex items-center rounded-full font-bold whitespace-nowrap uppercase tracking-wide',
  {
    variants: {
      tone: {
        neutral: 'bg-bg-elevated text-text-secondary',
        success: 'bg-status-success text-text-on-action',
        warning: 'bg-status-warning text-text-on-action',
        danger: 'bg-status-danger text-text-on-action',
        info: 'bg-status-info text-text-on-action',
        live: 'bg-live-bg text-live-indicator',
      },
      size: {
        sm: 'gap-1.5 px-2 py-1 text-caption',
        md: 'gap-1.5 px-3 py-2 text-body-s',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  },
);

export interface BadgeProps {
  children: ReactNode;
  /** Matches the Figma `Tone` property. */
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'live';
  size?: 'sm' | 'md';
  className?: string;
}

export function Badge({ children, tone = 'neutral', size, className }: BadgeProps) {
  const isLive = tone === 'live';

  if (isLive) {
    return (
      <span
        className={cn(
          'inline-flex h-[18px] items-center gap-1 rounded-full bg-live-bg px-1.5 text-live-indicator',
          className,
        )}
      >
        <Icon
          name="live-icon"
          size={12}
          className="!block size-3 shrink-0 -translate-y-px text-live-indicator motion-safe:animate-pulse"
        />
        <span className="text-[9px] leading-none font-bold uppercase tracking-wide">
          {children}
        </span>
      </span>
    );
  }

  return (
    <span className={cn(badge({ tone, size }), className)}>{children}</span>
  );
}
